import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, ArrowUpRight } from "lucide-react";
import { SEARCH_INDEX } from "../lib/site";
import { getLenis } from "../lib/smooth";

export const SearchNav = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return SEARCH_INDEX.slice(0, 6);
        return SEARCH_INDEX.filter((item) =>
            `${item.title} ${item.keywords} ${item.desc}`.toLowerCase().includes(q)
        ).slice(0, 8);
    }, [query]);

    useEffect(() => setActive(0), [results]);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        const lenis = getLenis();
        if (open) {
            lenis?.stop();
            document.body.style.overflow = "hidden";
            const t = setTimeout(() => inputRef.current?.focus(), 80);
            return () => clearTimeout(t);
        }
        lenis?.start();
        document.body.style.overflow = "";
        setQuery("");
    }, [open]);

    const go = (item) => {
        if (!item) return;
        setOpen(false);
        navigate(item.path);
    };

    const onInputKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            go(results[active]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <>
            <button
                type="button"
                data-testid="nav-search-button"
                aria-label="Search the website"
                onClick={() => setOpen(true)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#FDFBF7]/25 text-[#FDFBF7] transition-[border-color,color] duration-300 hover:border-[#C2A160] hover:text-[#C2A160]"
            >
                <Search size={17} aria-hidden="true" />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="search-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[70] bg-[#04150D]/85 px-5 pt-24 backdrop-blur-sm sm:pt-32"
                        onClick={() => setOpen(false)}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Site search"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: -14 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="mx-auto w-full max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-[#07211A] shadow-[0_40px_100px_rgba(0,0,0,0.5)]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
                                <Search size={18} className="shrink-0 text-[#C2A160]" aria-hidden="true" />
                                <input
                                    ref={inputRef}
                                    data-testid="search-input"
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={onInputKey}
                                    placeholder="Search GGH — framework, pledge, join, research…"
                                    aria-label="Search the website"
                                    className="w-full bg-transparent text-sm text-[#FDFBF7] placeholder:text-[#FDFBF7]/40 focus:outline-none"
                                />
                                <kbd className="hidden shrink-0 rounded-md border border-white/15 px-2 py-1 text-[10px] font-bold text-[#FDFBF7]/50 sm:block">
                                    ESC
                                </kbd>
                            </div>

                            {results.length > 0 ? (
                                <ul className="max-h-[55vh] overflow-y-auto py-2" data-testid="search-results">
                                    {results.map((item, i) => (
                                        <li key={item.path + item.title}>
                                            <button
                                                type="button"
                                                data-testid={`search-result-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                                onClick={() => go(item)}
                                                onMouseEnter={() => setActive(i)}
                                                className={`flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors duration-150 ${
                                                    i === active ? "bg-[#C2A160]/10" : ""
                                                }`}
                                            >
                                                <span>
                                                    <span className="block text-sm font-bold text-[#FDFBF7]">
                                                        {item.title}
                                                    </span>
                                                    <span className="mt-0.5 block text-xs text-[#FDFBF7]/55">
                                                        {item.desc}
                                                    </span>
                                                </span>
                                                <ArrowUpRight
                                                    size={15}
                                                    aria-hidden="true"
                                                    className={`shrink-0 transition-colors ${i === active ? "text-[#D9B96A]" : "text-[#FDFBF7]/25"}`}
                                                />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p data-testid="search-no-results" className="px-5 py-10 text-center text-sm text-[#FDFBF7]/60">
                                    No matches for “{query}”. Try “framework”, “join” or “pledge”.
                                </p>
                            )}

                            <div className="flex items-center gap-4 border-t border-white/10 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#FDFBF7]/40">
                                <span>↑↓ Navigate</span>
                                <span>Enter Open</span>
                                <span>Esc Close</span>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
